import { ensureAttachmentArray } from '@/utils/attachments';
import {
  RIGHT_TO_WORK_CHECK_METHOD_OPTIONS,
  RIGHT_TO_WORK_LIST_OPTIONS,
  deriveRightToWorkStatus,
  deriveRightToWorkCheckDeadline,
  getRightToWorkFollowUpBasis,
  isRightToWorkRepeatCheckRequired,
  formatRightToWorkStatusLabel,
  getRightToWorkDocumentChecklistOptions,
  getRightToWorkStatusHint,
} from '@/utils/rightToWork';

const normalizeCatalogName = (name = '') =>
  String(name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const normalizeCatalogKey = (catalogOrName = '') => {
  if (catalogOrName && typeof catalogOrName === 'object') {
    return normalizeCatalogName(catalogOrName.key ?? catalogOrName.name ?? '');
  }
  return normalizeCatalogName(catalogOrName ?? '');
};

const defineRow = (id, fields, options = {}) => ({ id, fields, ...options });

const cloneTemplateNode = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => cloneTemplateNode(entry));
  }
  if (value && typeof value === 'object') {
    const cloned = {};
    Object.entries(value).forEach(([key, entry]) => {
      cloned[key] = cloneTemplateNode(entry);
    });
    return cloned;
  }
  return value;
};

export const catalogTemplates = {
  clients: {
    key: 'clients',
    rows: [
      defineRow('row1', [
        { key: 'name', label: 'Name', type: 'text', required: true },
        {
          key: 'catalogDisplayName',
          label: 'Display in catalog as',
          type: 'text',
        },
      ]),
      defineRow('row2', [
        { key: 'customerId', label: 'Customer ID', type: 'text' },
        { key: 'terms', label: 'Terms', type: 'text' },
      ]),
      defineRow('row3', [
        { key: 'address', label: 'Address', type: 'textarea' },
      ]),
      defineRow('row4', [
        { key: 'email', label: 'Email', type: 'email' },
        { key: 'telephone', label: 'Telephone', type: 'text' },
      ]),
    ],
  },
  'clients-buildings': {
    key: 'clients-buildings',
    rows: [
      defineRow('row1', [
        { key: 'name', label: 'Name', type: 'text', required: true },
      ]),
      defineRow('row2', [
        { key: 'address', label: 'Address', type: 'textarea' },
      ]),
      defineRow('row3', [
        { key: 'attachments', label: 'Attachments', type: 'attachments' },
      ]),
    ],
  },
  services: {
    key: 'services',
    rows: [
      defineRow('row1', [
        { key: 'name', label: 'Name', type: 'text', required: true },
      ]),
      defineRow('row2', [
        {
          key: 'services_groups',
          label: 'Service groups',
          type: 'service-groups',
        },
      ]),
      defineRow('pricing', [
        { key: 'rate', label: 'Rate', type: 'number', prefix: '£' },
        { key: 'supplierRate', label: 'Subcontractor pay', type: 'number', prefix: '£' },
        { key: 'employeeRate', label: 'Employee pay', type: 'number', prefix: '£' },
      ], { layout: 'service-pricing' }),
    ],
  },
  suppliers: {
    key: 'suppliers',
    rows: [
      defineRow('row1', [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'telephone', label: 'Telephone (include country code)', type: 'text' },
      ], { section: 'general-info', sectionLabel: 'General info' }),
      defineRow('row2', [
        { key: 'address', label: 'Address', type: 'textarea' },
      ], { section: 'general-info', sectionLabel: 'General info' }),
      defineRow('row3', [
        { key: 'email', label: 'Email', type: 'email' },
        { key: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
      ], { section: 'general-info', sectionLabel: 'General info' }),
      defineRow('row4', [
        { key: 'utr', label: 'UTR', type: 'text' },
        { key: 'dbs', label: 'DBS', type: 'text' },
      ], { section: 'additional-info', sectionLabel: 'Additional info', layout: 'supplier-table' }),
      defineRow('row5', [
        {
          key: 'rightToWorkDocuments',
          label: 'Document verified',
          hideLabel: true,
          type: 'checkbox-table',
          checkboxTablePrimaryColumnLabel: 'Document Verified',
          optionsResolver: () => getRightToWorkDocumentChecklistOptions(),
        },
      ], { section: 'additional-info', sectionLabel: 'Additional info' }),
      defineRow('row6', [
        {
          key: 'rightToWorkSummary',
          label: 'Current status',
          type: 'computed-text',
          persist: false,
          compute: (model) => formatRightToWorkStatusLabel(deriveRightToWorkStatus(model)),
          hintResolver: getRightToWorkStatusHint,
        },
      ], {
        section: 'right-to-work-uk',
        sectionLabel: 'Right to work',
        subsection: 'status',
        subsectionLabel: 'Status overview',
      }),
      defineRow('row7', [
        {
          key: 'rightToWorkListType',
          label: 'Right to Work List',
          type: 'select',
          options: RIGHT_TO_WORK_LIST_OPTIONS,
        },
        {
          key: 'rightToWorkCheckMethod',
          label: 'Check Method Used',
          type: 'select',
          options: RIGHT_TO_WORK_CHECK_METHOD_OPTIONS,
        },
        { key: 'rightToWorkCheckedBy', label: 'Check Conducted By', type: 'text' },
        { key: 'rightToWorkCheckDate', label: 'Date of Check', type: 'date' },
        {
          key: 'rightToWorkFollowUpBasis', label: 'Follow-up basis', type: 'select', default: 'permission_expiry',
          options: [
            { value: 'permission_expiry', label: 'Permission expiry' },
            { value: 'euss_continuous', label: 'EUSS status granted — same employment, no repeat check' },
          ],
          visibleWhen: (model) => String(model.rightToWorkListType).trim() === 'list_b' && getRightToWorkFollowUpBasis(model) !== 'ecs_notice',
        },
        {
          key: 'rightToWorkPvnValidUntil', label: 'PVN valid until', type: 'date',
          hint: 'Use the validity end date stated in the Employer Checking Service notice.',
          visibleWhen: (model) => getRightToWorkFollowUpBasis(model) === 'ecs_notice',
        },
        {
          key: 'rightToWorkCheckDeadline', label: 'Next check by', type: 'computed-text', persist: false,
          compute: (model) => {
            if (!isRightToWorkRepeatCheckRequired(model)) return 'Not required during the same employment';
            const date = deriveRightToWorkCheckDeadline(model);
            return date ? date.split('-').reverse().join('.') : 'Not set';
          },
        },
      ], {
        section: 'right-to-work-uk',
        sectionLabel: 'Right to work',
        subsection: 'verification',
        subsectionLabel: 'Verification details',
      }),
      defineRow('row8', [
        { key: 'rightToWorkRoleAssignment', label: 'Role / Assignment', type: 'text' },
        {
          key: 'rightToWorkEngagementType',
          label: 'Engagement Type',
          type: 'select',
          options: [
            { value: 'self_employed', label: 'Self-employed' },
            { value: 'employee', label: 'Employee' },
          ],
        },
        {
          key: 'engagementEffectiveFrom',
          label: 'Engagement effective from',
          type: 'date',
          visibleWhen: { field: 'rightToWorkEngagementType', equals: 'employee' },
        },
      ], { section: 'general-info', sectionLabel: 'General info' }),
      defineRow('row9', [
        {
          key: 'buildings',
          label: 'Site assigned',
          type: 'client-select',
          options: [],
          multiple: true,
          placeholder: 'Select buildings',
        },
        { key: 'rightToWorkStartDate', label: 'Start Date', type: 'date' },
      ], { section: 'general-info', sectionLabel: 'General info' }),
      defineRow('row10', [
        {
          key: 'rightToWorkDocumentDate',
          label: 'Right to work from',
          type: 'date',
          visibleWhen: (model) => String(model.rightToWorkListType).trim() === 'list_b' && getRightToWorkFollowUpBasis(model) === 'permission_expiry',
        },
        {
          key: 'rightToWorkExpiryDate',
          label: 'Right to work until',
          type: 'date',
          visibleWhen: (model) => String(model.rightToWorkListType).trim() === 'list_b' && getRightToWorkFollowUpBasis(model) === 'permission_expiry',
        },
        {
          key: 'rightToWorkDocumentReference',
          label: 'Document reference number',
          type: 'text',
        },
      ], {
        section: 'right-to-work-uk',
        sectionLabel: 'Right to work',
        subsection: 'document',
        subsectionLabel: 'Document details',
      }),
      defineRow('row13', [
        {
          key: 'rightToWorkOutcome',
          label: 'Outcome / Notes',
          type: 'textarea',
        },
      ], {
        section: 'right-to-work-uk',
        sectionLabel: 'Right to work',
        subsection: 'notes',
        subsectionLabel: 'Notes',
      }),
      defineRow('row15', [
        { key: 'attachments', label: 'Attachments', type: 'attachments' },
      ]),
    ],
  },
  vendors: {
    key: 'vendors',
    rows: [
      defineRow('row1', [
        { key: 'name', label: 'Name', type: 'text', required: true },
      ]),
    ],
  },
  cos: {
    key: 'cos',
    rows: [
      defineRow('row1', [
        { key: 'name', label: 'Name', type: 'text', required: true },
      ]),
    ],
  },
};

export const getCatalogTemplate = (catalogOrName = '') => {
  const key = normalizeCatalogKey(catalogOrName);
  return catalogTemplates[key] ?? null;
};

export const cloneCatalogTemplate = (template) => {
  if (!template) return null;
  return cloneTemplateNode(template);
};

export const buildModelFromTemplate = (template, initial = {}) => {
  if (!template) return {};
  const model = {};
  template.rows.forEach((row) => {
    row.fields.forEach((field) => {
      if (field.persist === false) {
        return;
      }
      if (initial[field.key] !== undefined && initial[field.key] !== null) {
        if (field.type === 'attachments') {
          model[field.key] = ensureAttachmentArray(initial[field.key]);
        } else if (field.type === 'service-groups') {
          model[field.key] = Array.isArray(initial[field.key]) ? [...initial[field.key]] : [];
        } else if (field.type === 'client-select' && field.multiple) {
          model[field.key] = Array.isArray(initial[field.key]) ? [...initial[field.key]] : [];
        } else if (field.type === 'checkbox-table') {
          model[field.key] = initial[field.key] && typeof initial[field.key] === 'object'
            ? { ...initial[field.key] }
            : {};
        } else {
          model[field.key] = initial[field.key];
        }
      } else if (field.default !== undefined) {
        model[field.key] = field.default;
      } else if (field.type === 'attachments') {
        model[field.key] = [];
      } else if (field.type === 'service-groups') {
        model[field.key] = [];
      } else if (field.type === 'client-select' && field.multiple) {
        model[field.key] = [];
      } else if (field.type === 'checkbox-table') {
        model[field.key] = {};
      } else {
        model[field.key] = '';
      }
    });
  });
  return model;
};
